import React, { createContext, useEffect } from 'react';
import { lightTheme, darkTheme } from './constants';
import { useDispatch, useSelector } from 'react-redux';
import { updatePreferences } from 'store/Preferences/actions';
import { selectPreferences } from 'store/Preferences/selectors';
import { get } from 'lodash';
import { spacing, radius, typography } from 'shared/ui/tokens';

export const ThemeContext = createContext();

/**
 * Resolves the full theme object for a given theme name: the color palette
 * (`lightTheme`/`darkTheme`, from Context/constants.js) plus the theme-agnostic
 * design tokens (spacing/radius/typography, from shared/ui/tokens.js).
 *
 * Kept as a single composition point on purpose: `lightTheme`/`darkTheme` stay pure color
 * palettes, and any other place that still imports them directly (e.g.
 * Screens/.../GraphsAnalytics, which forces a raw `lightTheme` into its own ThemeContext
 * override to render charts for PDF export) does NOT receive these tokens — which is fine,
 * since that override only feeds the chart color hooks, never styled-components' own
 * `props.theme` (that one is fixed once, at the top of the app, by this function).
 *
 * @param {'light'|'dark'} themeName
 * @return {Object} the full theme object, ready for styled-components' ThemeProvider
 */
export const resolveThemeStyles = (themeName) => ({
  ...(themeName === 'light' ? lightTheme : darkTheme),
  spacing,
  radius,
  typography
});

export const ThemeProvider = ({ children }) => {
  const preferences = useSelector(selectPreferences);
  const theme = get(preferences, 'theme', 'light');
  const dispatch = useDispatch();

  useEffect(() => {
    if (!theme) return;
    const root = document.getElementById('root');
    if (!root) return;

    root.classList.remove('light-theme', 'dark-theme');

    root.classList.add(`${theme}-theme`);
  }, [theme]);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    dispatch(updatePreferences({ theme: newTheme }));
  };

  const resetTheme = () => {
    dispatch(updatePreferences({ theme: 'light' }));
  };

  const setParticularTheme = (particularTheme) => {
    dispatch(updatePreferences({ theme: particularTheme }));
  };

  return (
    <ThemeContext.Provider
      value={ {
        theme,
        toggleTheme,
        resetTheme,
        setParticularTheme,
        themeStyles: resolveThemeStyles(theme)
      } }
    >
      {children}
    </ThemeContext.Provider>
  );
};
